import { z } from 'zod';

/** A JSON value as `parseLosslessJson` reads it: an integer past 2^53 is a `bigint`. */
export type LosslessJson = string | number | bigint | boolean | null | LosslessJson[] | { [key: string]: LosslessJson };

/** What `stringifyLosslessJson` writes: a JSON value, with `undefined` properties left out. */
type LosslessJsonInput = LosslessJson | undefined | LosslessJsonInput[] | { [key: string]: LosslessJsonInput };

export const losslessJsonSchema: z.ZodType<LosslessJson> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.bigint(),
    z.boolean(),
    z.null(),
    z.array(losslessJsonSchema),
    z.record(z.string(), losslessJsonSchema),
  ]),
);

// Only plain digits name an exact integer; `1e20` or `5.0` stay numbers.
const INTEGER_SOURCE = /^-?\d+$/;

const keepLargeIntegers = (_key: string, value: LosslessJson, context?: { source?: string }) => {
  if (typeof value !== 'number' || !Number.isInteger(value) || Number.isSafeInteger(value)) {
    return value;
  }
  // An engine without `JSON.parse` source text has already rounded it: refuse rather than name another point.
  if (context?.source === undefined) {
    throw new Error('This browser cannot read integers past 2^53 exactly.');
  }
  return INTEGER_SOURCE.test(context.source) ? BigInt(context.source) : value;
};

const writeBigints = (_key: string, value: LosslessJsonInput) => {
  if (typeof value !== 'bigint') {
    return value;
  }
  // An engine without `JSON.rawJSON` cannot write it exactly: refuse rather than name another point.
  if (!JSON.rawJSON) {
    throw new Error('This browser cannot send integers past 2^53 exactly.');
  }
  return JSON.rawJSON(value.toString());
};

/**
 * `JSON.parse` that keeps integers past 2^53 exact, as `bigint`. A rounded point id names another point, so a delete
 * or a payload edit would hit the wrong one.
 */
export const readLosslessJson = (text: string) => JSON.parse(text, keepLargeIntegers) as LosslessJson;

/** `readLosslessJson`, then parses the result with `schema`. */
export const parseLosslessJson = <T>(text: string, schema: z.ZodType<T>): T => schema.parse(readLosslessJson(text));

/** `JSON.stringify` that writes a `bigint` back as the bare number it was parsed from. */
export const stringifyLosslessJson = (value: LosslessJsonInput, space?: number) =>
  JSON.stringify(value, writeBigints, space);
